import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { useLang } from "@/context/LanguageContext";
import { X, ZoomIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GALLERY_HERO = "https://static.prod-images.emergentagent.com/jobs/236e7b77-d75d-43f0-8b66-78eefb5d55fb/images/2cdceac825142ef04485b8f57a6765a51154608c9f6afd4abc0cb07a41bbaf35.jpeg";

export default function Gallery() {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const { t } = useLang();

  const CATEGORIES = [
    { label: t("gallery.all"), value: "all" },
    { label: t("gallery.industry"), value: "industry" },
    { label: t("gallery.plotsCat"), value: "plots" },
    { label: t("gallery.aerialView"), value: "aerial" },
  ];

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => {
      setGallery(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = gallery.filter((g) =>
    categoryFilter === "all" ? true : g.category === categoryFilter
  );

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="gallery" />
      <Navbar />

      <PageHero
        image={GALLERY_HERO}
        tag="Site Gallery · IIE Sigaddi"
        title={t("gallery.title")}
        subtitle={t("gallery.subtitle")}
        breadcrumb={{ home: t("nav.home"), current: t("gallery.breadcrumb") }}
      />

      <div className="bg-[#1E293B] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              onClick={() => setCategoryFilter(c.value)}
              data-testid={`gallery-filter-${c.value}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                categoryFilter === c.value
                  ? "bg-orange-500 text-white border-orange-500"
                  : "border-slate-600 text-slate-400 hover:border-orange-400 hover:text-orange-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#0F172A] py-12" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="gallery-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="gallery-item bg-slate-800 animate-pulse h-48 w-full" />
              ))}
            </div>
          ) : (
            <div className="gallery-grid">
              {filtered.map((item) => (
                <div
                  key={item.id}
                  className="gallery-item group relative cursor-pointer overflow-hidden"
                  onClick={() => setLightbox(item)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full object-cover gallery-img"
                    style={{ height: "auto", display: "block" }}
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1668301952339-53ba65165d2e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
                    }}
                  />
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/50 transition-all duration-300 flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-sm font-medium">{item.title}</p>
                    <p className="text-slate-300 text-xs capitalize">{item.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filtered.length === 0 && !loading && (
            <div className="text-center py-20 text-slate-400">
              <p>{t("gallery.noImages")}</p>
            </div>
          )}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-orange-400 transition-colors"
            onClick={() => setLightbox(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl max-h-[90vh] w-full">
            <img
              src={lightbox.image_url}
              alt={lightbox.title}
              className="w-full h-full object-contain max-h-[80vh]"
            />
            <div className="text-center mt-3">
              <p className="text-white font-medium">{lightbox.title}</p>
              <p className="text-slate-400 text-sm capitalize">{lightbox.category}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
